<template>
    <b-container fluid class="d-flex flex-column w-100 h-100">
        <b-tabs @input="setActiveTable('')" v-model="activeTab"
                active-nav-item-class="bg-white text-secondary" nav-class="text-dark"
                content-class="mt-0 mb-0"
                active-tab-class=""
                class="">

            <b-tab v-for="section in form.Sections" :key="section.Name" :title="section.Label" lazy
                   title-link-class="bg-gray-light border border-gray text-black py-1" class=" pt-2">
                <b-skeleton-wrapper :loading="isOverlayDisplayed" class="w-100 h-100 pt-5">

                    <!-- Table est de type ChildField -->
                    <TableDisplaying v-for="table in section.Fields" :key="table.Name" :SectionName="section.Name"
                                     :Name="table.Name" :TitleTable="table.Label" v-on:TableFocus="setActiveTable"
                                     :ActiveTable="activeTable"
                                     :Headers="table.Headers" :HeadersMask="table.HeadersMask"
                                     :ObjectType="table.ObjectType" :Rows="table.Value" class="pb-3"/>

					<!-- Mise en forme pendant le chargement des données -->
					<template #loading>
						<b-col align-self="center">
							<div class="b-skeleton w-100 b-skeleton-icon b-skeleton-animate-fade bg-transparent text-gray-middle-dark" align-v="center">
								<font-awesome-icon icon="table-cells" size="6x" class="w-100" />
							</div>
						</b-col>
					</template>
				</b-skeleton-wrapper>
			</b-tab>
			<!-- Boutons d'actions sur les tableaux enfants -->
			<template #tabs-end>
				<!-- ToolbarButtons : Barre d'outils avec des boutons -->
				<b-button-toolbar key-nav :aria-label="$t('ToolbarButtons')" class="ml-auto mr-0">
					<b-button-group>
						<!-- Bouton de menu de création d'objet -->
						<!-- Remarque : une b-dropdown aurait été plus adéquat que b-button mais cela posait pb avec le composant menu hsc-menu-button-menu pour la gestion des évènements-->
						<hsc-menu-style-custom v-if="$store.getters['CheckAction']('Add', 'ObjectSheet')" class="pr-1 h-100">
							<!-- menuZIndex élevé pour que le menu apparaisse au-dessus de tout composant -->
							<hsc-menu-button-menu :menuZIndex="1000" ref="buttonMenu" class="h-100">
								<!-- NewObject : Nouvel objet -->
								<b-button v-b-tooltip.hover :title="$t('NewObject')"  variant="outline-black" pill class="pl-1 pr-1 h-100 border-0"><b-icon icon="plus-lg" /><b-icon icon="caret-down-fill" class="p-1" /></b-button>
								<template slot="contextmenu">
									<ObjectMenu :Menu="createObjectMenu" :Constraints="constraints" :ObjectType="nodeObjectType" :ParentNode="getNode(nodeId)"
										v-on:CloseObjectMenuEvent="$refs.buttonMenu.$refs.menu.close(true)" />
								</template>
							</hsc-menu-button-menu>
						</hsc-menu-style-custom>

                        <!-- Emplacement de la dropdown de liste des en-têtes affichées selon le tableau contenant le focus (portal de TableDisplaying.vue) -->
                        <portal-target name="HeadersPortal"/>

                        <!-- Liste de choix des en-têtes du tableau à afficher ou masquer en version desactivée -->
                        <portal to="HeadersPortal" v-if="activeTable === ''">
                            <b-row>
                                <b-col v-if="$store.getters['CheckAction']('Delete', 'ObjectSheet')"
                                       class="p-0 pl-2 no-gutters">
                                    <!-- Bouton de suppression-->
                                    <b-button v-b-tooltip.hover :title="$t('Delete')" variant="outline-black"
                                              class="btn-circle ml-1 border-0" :disabled="true">
                                        <b-icon icon="x-lg"/>
                                    </b-button>
                                </b-col>
                                <b-col v-if="$store.getters['CheckAction']('Write', 'ObjectSheet')"
                                       class="p-0 no-gutters">
                                    <!-- Bouton pour dupliquer un objet -->
                                    <b-button v-b-tooltip.hover :title="$t('Duplicate')" variant="outline-black"
                                              class="btn-circle ml-1 border-0" :disabled="true"><i class="bi bi-copy"/>
                                    </b-button>
                                </b-col>
                                <b-col class="p-0 no-gutters">
                                    <!-- Bouton pour ouvrir une fiche objet -->
                                    <!-- OpenFile : Ouvrir la fiche -->
                                    <b-button v-b-tooltip.hover :title="$t('OpenForm')" variant="outline-black"
                                              class="btn-circle ml-1 m-0 border-0" :disabled="true">
                                        <b-icon icon="eye"/>
                                    </b-button>
                                </b-col>
                                <b-col class="p-0 no-gutters">
                                    <!-- Bouton de menu de sélection de colonnes (désactivé) -->
                                    <!-- ModifyColumns : Modifier les colonnes -->
                                    <b-dropdown v-b-tooltip.hover :title="$t('ModifyColumns')"
                                                class="mx-1 h-100 border-0" text="menu" variant="outline-black"
                                                menu-class="pt-1 pr-3" toggle-class="border-0 rounded-pill"
                                                :disabled="true">
                                        <template #button-content>
                                            <b-icon icon="layout-three-columns"/>
                                        </template>
                                    </b-dropdown>
                                </b-col>
                            </b-row>
                        </portal>
                    </b-button-group>
                </b-button-toolbar>
            </template>
        </b-tabs>
    </b-container>
</template>

<script lang="ts">
	import { Component, Prop, PropSync, Vue, Watch } from "vue-property-decorator";
	import Dictionary from "@/types/Dictionary";
	import { MAIN_FILTER_TREE } from '@/App.settings';
	import TableDisplaying from "@/components/commons/TableDisplaying.vue";
	import FormDraft from "@/types/fields/FormDraft";
	import ObjectMenu from "@/components/commons/ObjectMenu.vue";
	import MenuItem from "@/types/MenuItem";
	import HscMenuStyleCustom from "@/components/commons/HscMenuStyleCustom";
	import ObjectDisplayingModeEnum from "@/enums/ObjectDisplayingModeEnum";
	import { Getter } from "vuex-class";
	import locales from "./locales/Children.json";
	import TreeNode from "@/types/TreeNode";

/** Composant de gestion des noeuds enfants du SingleNode courant */
@Component({
    components: {
        TableDisplaying,
        ObjectMenu,
        'hsc-menu-style-custom': HscMenuStyleCustom("secondary")
    },
    i18n: {messages: locales}
})
export default class Children extends Vue {
    /** Indique si les champs du formulaire sont éditables ou en mode consultation */
    @Prop()
    public ConsultMode!: ObjectDisplayingModeEnum;

    /** Données de formulaire à charger */
    @PropSync('Form')
    public form!: FormDraft;

    /** Nom du tableau actif */
    private activeTable: string = "";

    /** Index de l'onglet sélectionné */
    private activeTab: number = -1;

    /** Détermine si l'overlay de chargement est affiché ou non  */
    private isOverlayDisplayed: boolean = false;

		@Getter('ObjectStore/GetNodeId')
		private nodeId!: string;

		@Getter('ObjectStore/GetObjectType')
		private nodeObjectType!: string;

		@Getter(MAIN_FILTER_TREE + '/Node')
		private getNode!: (nodeId: string) => TreeNode;

    @Watch('activeTab', {immediate: true})
    private async loadSection() {
        console.debug("Children - form : ", this.form);
        console.debug("Children - activeTab : ", this.activeTab);
        if (this.activeTab > -1 && this.form.Name == "Children" && this.form.Sections && this.form.Sections.length > 0	// && this.activeTab in this.form.Sections && this.form.Sections[this.activeTab] && this.form.Sections[this.activeTab].Name
        ) {	// Si un onglet est sélectionné
            // Pour Children : le nom de la section contient le type à charger

            let objectType: string = this.form.Sections[this.activeTab].Name;
            console.debug("Children- loadSection !!! : " + objectType);
            this.isOverlayDisplayed = true;
            await this.$store.dispatch("ObjectStore/LoadChildrenSection", {objectType: objectType}).then(() => {
                this.isOverlayDisplayed = false;
            }).catch((error) => {
                console.error(error);
                this.$bvToast.toast(this.$t('ErrorOccurred').toString(), {
                    title: this.$t('Error').toString(),
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            });
        }
    }

    /**
     * Défini le tableau actif (utilisé pour déterminer les listes des colonnes avec coches affichés dans FieldsPortal à partir de TableDisplaying.vue)
     * @param tableName Nom du tableau actif
     */
    private setActiveTable(tableName: string) {
        this.activeTable = tableName;
    }

    @Getter('MenuStore/GetMenuItems')
    private createObjectMenu!: MenuItem[];
    @Getter('MenuStore/GetConstraints')
    private constraints!: Dictionary<any>;

    async created() {
        console.debug("Children- created !!!");
    }
}
</script>

<style scoped>
/** Mise en forme des boutons d'onglets selon proposition Jujotte */
:deep(.nav li > *) {
    /* top-left | top-right | bottom-right | bottom-left */
    border-radius: 0 0 0 0 !important;
}

:deep(.nav li:first-of-type > *) {
    /* top-left | top-right | bottom-right | bottom-left */
    border-radius: 50rem 0 0 50rem !important;
}

:deep(.nav li:last-of-type > *) {
    /* top-left | top-right | bottom-right | bottom-left */
    border-radius: 0 50rem 50rem 0 !important;
}

:deep(.nav li:only-of-type > *) {
    /* top-left | top-right | bottom-right | bottom-left */
    border-radius: 50rem 50rem 50rem 50rem !important;
}

/** Change la couleur de l'onglet actif */
:deep(.nav-link.active) {
    color: var(--secondary) !important;
}
</style>